import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/price-books")({
  component: PriceBooksPage,
});

import { useState } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { PriceBookFormSheet } from "@/components/inventory/forms/PriceBookFormSheet";
import { PriceBookTable } from "@/components/inventory/tables/PriceBookTable";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import {
  useFetchPriceBooks,
  usePriceBookMutations,
} from "@/hooks/api/inventory/usePricing";
import type { PriceBook, PriceBookPayload } from "@/types/inventory";

function PriceBooksPage() {
  const navigate = useNavigate();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<PriceBook | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PriceBook | null>(null);
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const priceBooksQuery = useFetchPriceBooks({ page, page_size: pageSize });
  const priceBooks = priceBooksQuery.data?.results ?? [];
  const count = priceBooksQuery.data?.count ?? 0;

  const { createPriceBook, updatePriceBook, deletePriceBook } =
    usePriceBookMutations();

  const isMutating =
    createPriceBook.isPending ||
    updatePriceBook.isPending ||
    deletePriceBook.isPending;
  const isLoading = priceBooksQuery.isPending || isMutating;
  const isFetching = priceBooksQuery.isFetching || isMutating;

  const handleSubmit = async (data: PriceBookPayload) => {
    if (editing) {
      await updatePriceBook.mutateAsync({ id: editing.id, payload: data });
      toast.success("Price book updated");
    } else {
      await createPriceBook.mutateAsync(data);
      toast.success("Price book created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deletePriceBook.mutateAsync({ id: deleteTarget.id });
      toast.success("Price book deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Price Books"
          description="Manage price books used for item pricing"
          onAdd={() => {
            setEditing(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="New Price Book"
          isLoading={isFetching}
        />

        <PriceBookTable
          priceBooks={priceBooks}
          isLoading={isLoading}
          onView={(pb) => {
            setEditing(pb);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(pb) => {
            setEditing(pb);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(pb) => setDeleteTarget(pb)}
          onNameClick={(pb) =>
            navigate({
              to: "/admin/inventory/price-books/$id",
              params: { id: String(pb.id) },
            })
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          isLoading={isFetching}
        />

        <PriceBookFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
            setIsViewOnly(false);
          }}
          onSave={handleSubmit}
          editing={editing}
          isLoading={createPriceBook.isPending || updatePriceBook.isPending}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Price Book"
          description={`Delete price book "${deleteTarget?.name}"? All item prices linked to this book will also be deleted.`}
          itemName={deleteTarget?.name}
          isLoading={deletePriceBook.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
